import { AssigneeFilterComponent, AssigneeFilterValue, EMPTY_ASSIGNEE_FILTER } from '../../company/assignee-filter/assignee-filter';
import { CompanyContextService } from '../../../services/company-context';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CargoRequestService, CargoRequest, CargoRequestProduct, SearchCargoRequestsRequest } from '../../../services/api/cargo-request';
import { FreightForwarderBidService } from '../../../services/api/freight-forwarder-bid';
import { PageResponse } from '../../../services/api/page-response';
import { DictionaryService, CountryDto } from '../../../services/api/dictionary';
import { AuthService } from '../../../services/auth/auth';
import { LanguageService } from '../../../services/i18n/language';
import { CountrySelectComponent } from '../../shared/country-select/country-select';
import { FfCabinetHeadComponent } from '../cabinet-head/cabinet-head.component';
import { Subscription } from 'rxjs';
import { formatRequestNumber } from '../../../services/request-number';

@Component({
  selector: 'app-freight-forwarder-requests-list',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, AssigneeFilterComponent, CountrySelectComponent, FfCabinetHeadComponent],
  templateUrl: './freight-forwarder-requests-list.html',
  styleUrl: './freight-forwarder-requests-list.scss'
})
export class FreightForwarderRequestsListComponent implements OnInit, OnDestroy {
  requests: CargoRequest[] = [];
  loading = false;
  error: string | null = null;
  
  filterForm: FormGroup;
  
  // Pagination
  currentPage = 0; // для внутреннего использования
  pageSize = 20; // для внутреннего использования
  totalElements = 0;
  totalPages = 0;
  
  // Dictionary data
  countries: CountryDto[] = [];
  currencies: any[] = [];
  rateTypes: Array<{ value: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER'; label: string }> = [
    { value: 'FTL', label: 'FTL (Выделенный АТС)' },
    { value: 'LTL', label: 'LTL (Сборный груз)' },
    { value: 'AIR', label: 'Авиа' },
    { value: 'CONTAINER', label: 'Контейнер' }
  ];
  
  // Translations
  titleText = 'Запросы на перевозку';
  /** Блок фильтров по умолчанию свёрнут; открывается кнопкой «Фильтры». */
  filtersOpen = false;
  searchText = 'Поиск';
  clearFiltersText = 'Очистить фильтры';
  requestNumberText = 'Номер запроса';
  routeText = 'Маршрут';
  totalWeightText = 'Общий вес';
  totalVolumeText = 'Общий объем';
  loadingDateText = 'Дата погрузки';
  priceText = 'Стоимость груза';
  bidsCountText = 'Количество ставок';
  awaitingBidText = 'Ожидает ставку';
  statusText = 'Статус';
  dealExistsText = 'Сделка создана';
  bidSentText = 'Моя ставка';
  availableText = 'Доступно';
  noRequestsText = 'Запросы не найдены';
  loadingText = 'Загрузка...';
  rateTypeText = 'Тип ставки';
  fromCountryText = 'Страна отправления';
  fromCityText = 'Город отправления';
  toCountryText = 'Страна назначения';
  toCityText = 'Город назначения';
  loadingDateFromText = 'Дата погрузки от';
  loadingDateToText = 'Дата погрузки до';
  priceFromText = 'Стоимость груза от';
  priceToText = 'Стоимость груза до';
  currencyText = 'Валюта';
  calculateCustomsPaymentsText = 'Расчет таможенных платежей';
  selectAllText = 'Все';
  viewText = 'Просмотр';
  makeBidText = 'Сделать ставку';
  previousPageText = 'Предыдущая';
  nextPageText = 'Следующая';
  pageInfoText = 'Страница';
  ofText = 'из';
  totalText = 'всего';
  yesText = 'Да';
  noText = 'Нет';
  
  myBidRequestIds: Set<number> = new Set();
  private subscriptions = new Subscription();

  constructor(
    private cargoRequestService: CargoRequestService,
    private bidService: FreightForwarderBidService,
    private dictionaryService: DictionaryService,
    private authService: AuthService,
    private router: Router,
    private fb: FormBuilder,
    private languageService: LanguageService,
    public companyContext: CompanyContextService,
    private cdr: ChangeDetectorRef
  ) {
    this.filterForm = this.fb.group({
      rateType: [null],
      fromCountryId: [null],
      fromCity: [''],
      toCountryId: [null],
      toCity: [''],
      loadingDateFrom: [''],
      loadingDateTo: [''],
      priceFrom: [null],
      priceTo: [null],
      currencyId: [null],
      calculateCustomsPayments: [null]
    });
  }

  ngOnInit(): void {
    this.companyContext.ensureLoaded();
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('FreightForwarderRequestsList: Running on server (SSR), skipping initialization');
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        console.warn('FreightForwarderRequestsList: User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      const user = this.authService.getUser();
      if (user?.role?.code !== 'ROLE_FREIGHT_FORWARDER') {
        console.warn('FreightForwarderRequestsList: User is not freight forwarder, redirecting to home');
        this.router.navigate(['/']);
        return;
      }

      this.loadDictionaries();
      this.loadMyBidRequestIds();
      this.updateTranslations();
      this.searchRequests();
      
      this.subscriptions.add(
        this.languageService.getTranslations().subscribe(() => {
          this.updateTranslations();
        })
      );
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.yesText = this.languageService.translate('common.yes') || this.yesText;
    this.noText = this.languageService.translate('common.no') || this.noText;
    this.titleText = this.languageService.translate('freightForwarder.requests.title') || 'Запросы на перевозку';
    this.searchText = this.languageService.translate('freightForwarder.requests.search') || 'Поиск';
    this.clearFiltersText = this.languageService.translate('freightForwarder.requests.clearFilters') || 'Очистить фильтры';
    this.requestNumberText = this.languageService.translate('freightForwarder.requests.requestNumber') || 'Номер запроса';
    this.routeText = this.languageService.translate('freightForwarder.requests.route') || 'Маршрут';
    this.totalWeightText = this.languageService.translate('freightForwarder.requests.totalWeight') || 'Общий вес';
    this.totalVolumeText = this.languageService.translate('freightForwarder.requests.totalVolume') || 'Общий объем';
    this.loadingDateText = this.languageService.translate('freightForwarder.requests.loadingDate') || 'Дата погрузки';
    this.priceText = this.languageService.translate('freightForwarder.requests.price') || 'Стоимость груза';
    this.bidsCountText = this.languageService.translate('freightForwarder.requests.bidsCount') || 'Количество ставок';
    this.awaitingBidText = this.languageService.translate('freightForwarder.requests.awaitingBid') || 'Ожидает ставку';
    this.statusText = this.languageService.translate('freightForwarder.requests.status') || 'Статус';
    this.dealExistsText = this.languageService.translate('freightForwarder.requests.dealExists') || 'Сделка создана';
    this.bidSentText = this.languageService.translate('freightForwarder.requests.bidSent') || 'Моя ставка';
    this.availableText = this.languageService.translate('freightForwarder.requests.available') || 'Доступно';
    this.noRequestsText = this.languageService.translate('freightForwarder.requests.noRequests') || 'Запросы не найдены';
    this.loadingText = this.languageService.translate('freightForwarder.requests.loading') || 'Загрузка...';
    this.rateTypeText = this.languageService.translate('freightForwarder.requests.rateType') || 'Тип ставки';
    this.fromCountryText = this.languageService.translate('freightForwarder.requests.fromCountry') || 'Страна отправления';
    this.fromCityText = this.languageService.translate('freightForwarder.requests.fromCity') || 'Город отправления';
    this.toCountryText = this.languageService.translate('freightForwarder.requests.toCountry') || 'Страна назначения';
    this.toCityText = this.languageService.translate('freightForwarder.requests.toCity') || 'Город назначения';
    this.loadingDateFromText = this.languageService.translate('freightForwarder.requests.loadingDateFrom') || 'Дата погрузки от';
    this.loadingDateToText = this.languageService.translate('freightForwarder.requests.loadingDateTo') || 'Дата погрузки до';
    this.priceFromText = this.languageService.translate('freightForwarder.requests.priceFrom') || 'Стоимость груза от';
    this.priceToText = this.languageService.translate('freightForwarder.requests.priceTo') || 'Стоимость груза до';
    this.currencyText = this.languageService.translate('freightForwarder.requests.currency') || 'Валюта';
    this.calculateCustomsPaymentsText = this.languageService.translate('freightForwarder.requests.calculateCustomsPayments') || 'Расчет таможенных платежей';
    this.selectAllText = this.languageService.translate('freightForwarder.requests.selectAll') || 'Все';
    this.viewText = this.languageService.translate('freightForwarder.requests.view') || 'Просмотр';
    this.makeBidText = this.languageService.translate('freightForwarder.requests.makeBid') || 'Сделать ставку';
    this.previousPageText = this.languageService.translate('freightForwarder.requests.previousPage') || 'Предыдущая';
    this.nextPageText = this.languageService.translate('freightForwarder.requests.nextPage') || 'Следующая';
    this.pageInfoText = this.languageService.translate('freightForwarder.requests.pageInfo') || 'Страница';
    this.ofText = this.languageService.translate('freightForwarder.requests.of') || 'из';
    this.totalText = this.languageService.translate('freightForwarder.requests.total') || 'всего';
    this.cdr.markForCheck();
  }

  loadDictionaries(): void {
    this.subscriptions.add(
      this.dictionaryService.getAllCountries().subscribe({
        next: (countries) => {
          this.countries = countries;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading countries', err);
        }
      })
    );

    this.subscriptions.add(
      this.dictionaryService.getCurrencies().subscribe({
        next: (currencies) => {
          this.currencies = currencies;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading currencies', err);
        }
      })
    );
  }

  loadMyBidRequestIds(): void {
    this.subscriptions.add(
      this.bidService.getAllBidsPaginated(0, 500).subscribe({
        next: (pageResponse) => {
          const bids = pageResponse.content || [];
          this.myBidRequestIds = new Set(bids.map(b => b.cargoRequestId));
          this.cdr.markForCheck();
        },
        error: (err) => console.warn('FreightForwarderRequestsList: Could not load my bids', err)
      })
    );
  }

  getRequestStatusText(request: CargoRequest): string {
    if (request.hasDeal) return this.dealExistsText;
    if (this.myBidRequestIds.has(request.id)) return this.bidSentText;
    return this.availableText;
  }

  get filtersTitle(): string {
    const value = this.languageService.translate('freightForwarder.requests.filters');
    return value && value !== 'freightForwarder.requests.filters' ? value : 'Фильтры';
  }

  /** Цвет метки заказа: доступен — зелёный, ставка отправлена — синий, уже есть сделка — жёлтый. */
  requestTone(request: CargoRequest): string {
    const cls = this.getRequestStatusClass(request);
    return cls === 'deal-exists' ? 'amber' : 'open';
  }

  getRequestStatusClass(request: CargoRequest): string {
    if (request.hasDeal) return 'deal-exists';
    if (this.myBidRequestIds.has(request.id)) return 'bid-sent';
    return 'available';
  }

  searchRequests(): void {
    this.loading = true;
    this.error = null;
    this.currentPage = 0;

    const formValue = this.filterForm.value;
    const request: SearchCargoRequestsRequest = {
      status: 'PENDING',
      rateType: formValue.rateType || undefined,
      fromCountryId: formValue.fromCountryId || undefined,
      fromCity: formValue.fromCity?.trim() || undefined,
      toCountryId: formValue.toCountryId || undefined,
      toCity: formValue.toCity?.trim() || undefined,
      loadingDateFrom: formValue.loadingDateFrom || undefined,
      loadingDateTo: formValue.loadingDateTo || undefined,
      priceFrom: formValue.priceFrom ? parseFloat(formValue.priceFrom) : undefined,
      priceTo: formValue.priceTo ? parseFloat(formValue.priceTo) : undefined,
      currencyId: formValue.currencyId || undefined,
      calculateCustomsPayments: formValue.calculateCustomsPayments !== null && formValue.calculateCustomsPayments !== '' ? formValue.calculateCustomsPayments : undefined,
      forwarderAssigneeId: this.managerFilter.id ?? undefined,
      forwarderAssigneeUnassigned: this.managerFilter.unassigned || undefined
    };

    this.subscriptions.add(
      this.cargoRequestService.searchCargoRequests(request, this.currentPage, this.pageSize).subscribe({
        next: (response: PageResponse<CargoRequest>) => {
          this.requests = response.content;
          this.totalElements = response.totalElements;
          this.totalPages = response.totalPages;
          this.currentPage = response.number;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('FreightForwarderRequestsList: Error searching requests', err);
          if (err.status === 403) {
            this.error = 'Ошибка доступа (403). Проверьте, что вы авторизованы как экспедитор.';
          } else if (err.status === 401) {
            this.error = 'Сессия истекла. Пожалуйста, войдите снова.';
            this.router.navigate(['/login']);
          } else {
            this.error = 'Ошибка при поиске запросов';
          }
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  /** Фильтр по менеджеру своей компании (для администратора компании-экспедитора). */
  managerFilter: AssigneeFilterValue = EMPTY_ASSIGNEE_FILTER;

  get canFilterManagers(): boolean {
    return this.companyContext.hasPermission('ASSIGN_REQUESTS');
  }

  onManagerFilter(value: AssigneeFilterValue): void {
    this.managerFilter = value;
    this.searchRequests();
  }

  clearFilters(): void {
    this.managerFilter = EMPTY_ASSIGNEE_FILTER;
    this.filterForm.reset({
      rateType: null,
      fromCountryId: null,
      fromCity: '',
      toCountryId: null,
      toCity: '',
      loadingDateFrom: '',
      loadingDateTo: '',
      priceFrom: null,
      priceTo: null,
      currencyId: null,
      calculateCustomsPayments: null
    });
    this.searchRequests();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    const formValue = this.filterForm.value;
    const request: SearchCargoRequestsRequest = {
      status: 'PENDING',
      rateType: formValue.rateType || undefined,
      fromCountryId: formValue.fromCountryId || undefined,
      fromCity: formValue.fromCity?.trim() || undefined,
      toCountryId: formValue.toCountryId || undefined,
      toCity: formValue.toCity?.trim() || undefined,
      loadingDateFrom: formValue.loadingDateFrom || undefined,
      loadingDateTo: formValue.loadingDateTo || undefined,
      priceFrom: formValue.priceFrom ? parseFloat(formValue.priceFrom) : undefined,
      priceTo: formValue.priceTo ? parseFloat(formValue.priceTo) : undefined,
      currencyId: formValue.currencyId || undefined,
      calculateCustomsPayments: formValue.calculateCustomsPayments !== null && formValue.calculateCustomsPayments !== '' ? formValue.calculateCustomsPayments : undefined,
      forwarderAssigneeId: this.managerFilter.id ?? undefined,
      forwarderAssigneeUnassigned: this.managerFilter.unassigned || undefined
    };

    this.loading = true;
    this.subscriptions.add(
      this.cargoRequestService.searchCargoRequests(request, this.currentPage, this.pageSize).subscribe({
        next: (response: PageResponse<CargoRequest>) => {
          this.requests = response.content;
          this.totalElements = response.totalElements;
          this.totalPages = response.totalPages;
          this.currentPage = response.number;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading page', err);
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  // ─── Карточки запросов ────────────────────────────────────────────────────

  /** Номер запроса для людей: BL-26-84. */
  requestNo(r: { id: number; createdAt?: string }): string {
    return formatRequestNumber(r.id, r.createdAt);
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  private get numberLocale(): string {
    const lang = this.languageService.getCurrentLanguageValue();
    return ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
  }

  /** Короткое понятное название типа перевозки. */
  rateLabel(rateType: string | null | undefined): string {
    switch (rateType) {
      case 'FTL': return this.t('ffSearch.rateFtl', 'Фура (FTL)');
      case 'LTL': return this.t('ffSearch.rateLtl', 'Сборный (LTL)');
      case 'AIR': return this.t('ffSearch.rateAir', 'Авиа');
      case 'CONTAINER': return this.t('ffSearch.rateContainer', 'Контейнер');
      default: return rateType || '';
    }
  }

  countryName(countryId: number): string {
    const c = this.countries.find(x => x.id === countryId);
    if (!c) return '';
    return this.languageService.getCurrentLanguageValue() === 'en' ? (c.nameEn || c.nameRu) : (c.nameRu || c.nameEn);
  }

  fmtWeight(kg: number): string {
    if (!kg) return '—';
    // Всегда в килограммах — экспедиторам так привычнее сравнивать
    return `${new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 1 }).format(kg)} ${this.t('ffSearch.kg', 'кг')}`;
  }

  fmtVolume(m3: number): string {
    if (!m3) return '—';
    return new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: m3 < 1 ? 3 : 1 }).format(m3) + ' м³';
  }

  fmtMoney(amount: number, currencyId: number): string {
    const code = this.currencies.find(c => c.id === currencyId)?.code || '';
    return `${new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 0 }).format(Math.round(amount || 0))} ${code}`.trim();
  }

  placesCount(r: CargoRequest): number {
    return (r.products ?? []).reduce((sum, p) => sum + (p.quantity || 1), 0);
  }

  hasFlag(r: CargoRequest, flag: keyof Pick<CargoRequestProduct, 'isDangerous' | 'isOversized' | 'isNonStackable'>): boolean {
    return (r.products ?? []).some(p => !!p[flag]);
  }

  /** open — можно ставить; mine — ставка уже есть; deal — перевозчик выбран. */
  cardState(r: CargoRequest): 'open' | 'mine' | 'deal' {
    if (r.hasDeal) return 'deal';
    return this.myBidRequestIds.has(r.id) ? 'mine' : 'open';
  }

  cardStatus(r: CargoRequest): string {
    switch (this.cardState(r)) {
      case 'deal': return this.t('ffSearch.statusDeal', 'Перевозчик выбран');
      default: return this.t('ffSearch.statusOpen', 'Принимает ставки');
    }
  }

  /** Сколько условий задано в «Ещё фильтры» (тип перевозки — отдельными кнопками). */
  get extraFiltersCount(): number {
    const v = this.filterForm.value;
    return ['fromCountryId', 'fromCity', 'toCountryId', 'toCity', 'loadingDateFrom', 'loadingDateTo', 'priceFrom', 'priceTo', 'currencyId', 'calculateCustomsPayments']
      .filter(k => v[k] !== null && v[k] !== undefined && v[k] !== '').length;
  }

  setRateType(value: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER' | null): void {
    this.filterForm.patchValue({ rateType: value });
    this.searchRequests();
  }

  openRequest(r: CargoRequest): void {
    if (!r.hasDeal) {
      this.viewRequest(r.id);
    }
  }

  makeBid(r: CargoRequest): void {
    this.router.navigate(['/freight-forwarder/create-bid', r.id], { queryParams: { returnTo: 'requests' } });
  }

  getRoute(request: CargoRequest): string {
    const fromCountry = this.countries.find(c => c.id === request.fromCountryId);
    const toCountry = this.countries.find(c => c.id === request.toCountryId);
    
    const fromCode = fromCountry?.code || '?';
    const toCode = toCountry?.code || '?';
    
    return `${request.fromCity} (${fromCode}) → ${request.toCity} (${toCode})`;
  }

  getTotalWeight(request: CargoRequest): number {
    return request.products.reduce((sum, product) => sum + product.totalWeightKg, 0);
  }

  getTotalVolume(request: CargoRequest): number {
    return request.products.reduce((sum, product) => {
      // Если есть volumeM3, используем его
      if (product.volumeM3) {
        return sum + product.volumeM3;
      }
      // Иначе вычисляем из габаритов одного места × количество мест
      if (product.lengthMm && product.widthMm && product.heightMm) {
        const volume = (product.lengthMm * product.widthMm * product.heightMm) / 1_000_000_000 * (product.quantity || 1); // Convert to m³
        return sum + volume;
      }
      // Если ни габариты, ни объем не заполнены, возвращаем 0
      return sum;
    }, 0);
  }

  formatWeight(weight: number): string {
    if (weight >= 1000) {
      return `${(weight / 1000).toFixed(2)} т`;
    }
    return `${weight.toFixed(2)} кг`;
  }

  formatVolume(volume: number): string {
    const decimals = volume >= 1 ? 2 : volume >= 0.01 ? 4 : volume >= 0.0001 ? 6 : 8;
    return `${volume.toFixed(decimals)} м³`;
  }

  formatPrice(price: number, currencyId: number): string {
    const currency = this.currencies.find(c => c.id === currencyId);
    const currencyCode = currency?.code || '?';
    return `${price.toFixed(2)} ${currencyCode}`;
  }

  viewRequest(id: number): void {
    // Передаем параметр returnTo, чтобы знать, откуда вернуться
    this.router.navigate(['/freight-forwarder/request', id], {
      queryParams: { returnTo: 'requests' }
    });
  }
}
